/**
 * ConversacionesDashboard — Historial de conversaciones por WhatsApp.
 *
 * Reskin estilo Pacientes/SmartFlowsList: fondo plano, cards sólidas blancas,
 * header h5 navy, búsqueda dominante, filter chips segmented navy gradient,
 * lista en Paper con sticky headers por fecha, avatar gradient por usuario.
 *
 * Lógica preservada: fetch, polling, expand inline, CSV export, PDF download,
 * copy phone, filtros (chatbot, resultado, quick date), debounce search.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, IconButton, Chip, Tooltip, Skeleton, Collapse,
  MenuItem, Select, FormControl, TextField, InputAdornment, Button,
  CircularProgress, Snackbar, Alert, Avatar,
} from "@mui/material";
import { auth } from "../firebase";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import TimerOffIcon from "@mui/icons-material/TimerOff";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ForumIcon from "@mui/icons-material/Forum";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DownloadIcon from "@mui/icons-material/Download";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TableChartIcon from "@mui/icons-material/TableChart";
import TokenIcon from "@mui/icons-material/Token";

const API_URL = process.env.REACT_APP_API_URL || "https://turismo.nara.chat/api";

// ──────────────────────────────────────────────────────────────────────────
// Configs visuales
// ──────────────────────────────────────────────────────────────────────────
const RESULTADO_CONFIG = {
  completada: { color: "#10b981", label: "Completada", icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
  escalada:   { color: "#f59e0b", label: "Escalada",   icon: <WarningAmberIcon sx={{ fontSize: 12 }} /> },
  timeout:    { color: "#64748b", label: "Timeout",    icon: <TimerOffIcon sx={{ fontSize: 12 }} /> },
  activa:     { color: "#0ea5e9", label: "Activa",     icon: <FiberManualRecordIcon sx={{ fontSize: 11 }} /> },
};

const TEMA_COLOR = {
  sintomas:     { bg: "#fee2e2", fg: "#991b1b", label: "Síntomas" },
  cita:         { bg: "#dbeafe", fg: "#1e40af", label: "Cita" },
  citas:        { bg: "#dbeafe", fg: "#1e40af", label: "Citas" },
  informacion:  { bg: "#cffafe", fg: "#155e75", label: "Info" },
  registro:     { bg: "#dcfce7", fg: "#166534", label: "Registro" },
  escalacion:   { bg: "#fef3c7", fg: "#92400e", label: "Escalación" },
  saludo:       { bg: "#ede9fe", fg: "#5b21b6", label: "Saludo" },
  conversacion: { bg: "#f1f5f9", fg: "#475569", label: "Chat" },
  memoria:      { bg: "#fce7f3", fg: "#9f1239", label: "Memoria" },
};
const temaInfo = (t) => TEMA_COLOR[t] || { bg: "#f1f5f9", fg: "#475569", label: t };

// Avatar determinístico por nombre del paciente (mismo helper que Pacientes/SmartFlows)
const AVATAR_COLORS = [
  { bg: "#e0e7ff", fg: "#3730a3" }, { bg: "#fce7f3", fg: "#9f1239" },
  { bg: "#cffafe", fg: "#155e75" }, { bg: "#dcfce7", fg: "#166534" },
  { bg: "#fef3c7", fg: "#92400e" }, { bg: "#ede9fe", fg: "#5b21b6" },
  { bg: "#dbeafe", fg: "#1e40af" }, { bg: "#fee2e2", fg: "#991b1b" },
];
function hashColor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function iniciales(nombre) {
  if (!nombre) return "?";
  const parts = nombre.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers de fecha
// ──────────────────────────────────────────────────────────────────────────
const formatAbsTime = (isoStr) => {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  const day = d.toLocaleDateString("es", { day: "2-digit", month: "short" });
  const time = d.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" });
  return `${day} ${time}`;
};
const formatRelativeTime = (isoStr) => {
  if (!isoStr) return "";
  const now = new Date();
  const date = new Date(isoStr);
  const diffMs = now - date;
  if (diffMs < 0) return formatAbsTime(isoStr);
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return "ahora";
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffH < 24) return `hace ${diffH}h`;
  if (diffD < 7) return `hace ${diffD}d`;
  return formatAbsTime(isoStr);
};
const getDateGroup = (conv) => {
  if (conv.date_group) return conv.date_group;
  const isoStr = conv.inicio;
  if (!isoStr) return "Sin fecha";
  const d = new Date(isoStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Hoy";
  if (d.toDateString() === yesterday.toDateString()) return "Ayer";
  return d.toLocaleDateString("es", { weekday: "long", day: "2-digit", month: "short" });
};

// ──────────────────────────────────────────────────────────────────────────
// Componente principal
// ──────────────────────────────────────────────────────────────────────────
export default function ConversacionesDashboard() {
  const navigate = useNavigate();

  const [conversaciones, setConversaciones] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedMsgs, setExpandedMsgs] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [userId, setUserId] = useState(localStorage.getItem("user_id"));

  // Filtros
  const [chatbots, setChatbots] = useState([]);
  const [chatbotFilter, setChatbotFilter] = useState("");
  const [resultadoFilter, setResultadoFilter] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeQuickFilter, setActiveQuickFilter] = useState("semana");
  const [total, setTotal] = useState(0);
  const [activas, setActivas] = useState(0);

  // Feedback
  const [copiedPhone, setCopiedPhone] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  // Auto-refresh
  const autoRefreshRef = useRef(null);
  const prevTotalRef = useRef(0);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(busqueda), 400);
    return () => clearTimeout(timer);
  }, [busqueda]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) { navigate("/login"); return; }
      if (!userId && user.uid) {
        try {
          const res = await fetch(`${API_URL}/users/by_uid/${user.uid}`);
          if (res.ok) {
            const data = await res.json();
            if (data.id) { localStorage.setItem("user_id", data.id); setUserId(data.id); }
          }
        } catch (e) { console.error("Error getting user_id:", e); }
      }
    });
    return () => unsubscribe();
  }, [navigate, userId]);

  const getHeaders = useCallback(() => {
    return userId ? { "X-User-Id": String(userId), "Content-Type": "application/json" } : {};
  }, [userId]);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const headers = getHeaders();
      const params = new URLSearchParams();
      if (chatbotFilter) params.append("chatbot_id", chatbotFilter);
      if (resultadoFilter) params.append("resultado", resultadoFilter);
      if (activeQuickFilter && activeQuickFilter !== "todos") {
        params.append("quick_filter", activeQuickFilter);
      } else if (!activeQuickFilter) {
        if (fechaDesde) params.append("fecha_desde", fechaDesde);
        if (fechaHasta) params.append("fecha_hasta", fechaHasta);
      }
      if (searchDebounced) params.append("busqueda", searchDebounced);
      // Multi-tenant: superadmin envía la empresa activa para filtrar los chatbots
      const empresaActiva = localStorage.getItem("empresa_activa");
      if (empresaActiva) params.append("empresa_id", empresaActiva);
      params.append("limit", "50");
      params.append("offset", "0");

      const resumenParams = new URLSearchParams();
      if (chatbotFilter) resumenParams.append("chatbot_id", chatbotFilter);
      if (empresaActiva) resumenParams.append("empresa_id", empresaActiva);

      const [convRes, resumenRes] = await Promise.all([
        fetch(`${API_URL}/conversaciones/recientes?${params}`, { headers }),
        fetch(`${API_URL}/conversaciones/resumen-dia?${resumenParams}`, { headers }),
      ]);

      if (convRes.ok) {
        const data = await convRes.json();
        setConversaciones(data.conversaciones || []);
        setTotal(data.total || 0);
        setActivas(data.activas || 0);

        if (silent && prevTotalRef.current > 0 && data.total > prevTotalRef.current) {
          const nuevas = data.total - prevTotalRef.current;
          setSnackbar({ open: true, message: `${nuevas} nueva${nuevas > 1 ? "s" : ""} conversación${nuevas > 1 ? "es" : ""}` });
        }
        prevTotalRef.current = data.total;

        const cbMap = {};
        (data.conversaciones || []).forEach((c) => {
          if (c.chatbot_id && c.chatbot_name) cbMap[c.chatbot_id] = c.chatbot_name;
        });
        setChatbots(Object.entries(cbMap).map(([id, name]) => ({ id: Number(id), name })));
      }
      if (resumenRes.ok) setResumen(await resumenRes.json());
    } catch (err) {
      console.error("Error fetching conversaciones:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getHeaders, chatbotFilter, resultadoFilter, fechaDesde, fechaHasta, searchDebounced, activeQuickFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh cada 30s cuando hay activas
  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (activas > 0) {
      autoRefreshRef.current = setInterval(() => fetchData(true), 30000);
    }
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [activas, fetchData]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const headers = getHeaders();
      const params = new URLSearchParams();
      if (chatbotFilter) params.append("chatbot_id", chatbotFilter);
      if (resultadoFilter) params.append("resultado", resultadoFilter);
      if (activeQuickFilter && activeQuickFilter !== "todos") {
        params.append("quick_filter", activeQuickFilter);
      } else if (!activeQuickFilter) {
        if (fechaDesde) params.append("fecha_desde", fechaDesde);
        if (fechaHasta) params.append("fecha_hasta", fechaHasta);
      }
      if (searchDebounced) params.append("busqueda", searchDebounced);
      const empresaActiva = localStorage.getItem("empresa_activa");
      if (empresaActiva) params.append("empresa_id", empresaActiva);
      params.append("limit", "50");
      params.append("offset", String(conversaciones.length));

      const res = await fetch(`${API_URL}/conversaciones/recientes?${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setConversaciones((prev) => [...prev, ...(data.conversaciones || [])]);
      }
    } catch (err) { console.error("Error loading more:", err); }
    finally { setLoadingMore(false); }
  };

  const loadMessages = async (convId) => {
    if (expandedId === convId) { setExpandedId(null); return; }
    setExpandedId(convId);
    setLoadingMsgs(true);
    try {
      const empresaActiva = localStorage.getItem("empresa_activa");
      const qs = empresaActiva ? `?empresa_id=${encodeURIComponent(empresaActiva)}` : "";
      const res = await fetch(`${API_URL}/conversaciones/${convId}/mensajes${qs}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setExpandedMsgs(data.mensajes || []);
      }
    } catch (err) { console.error("Error loading messages:", err); }
    finally { setLoadingMsgs(false); }
  };

  const downloadPDF = async (convId, e) => {
    e.stopPropagation();
    try {
      const empresaActiva = localStorage.getItem("empresa_activa");
      const qs = empresaActiva ? `?empresa_id=${encodeURIComponent(empresaActiva)}` : "";
      const res = await fetch(`${API_URL}/conversaciones/${convId}/pdf${qs}`, { headers: getHeaders() });
      if (!res.ok) throw new Error("Error al descargar");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] || `conversacion_${convId}.pdf`;
      document.body.appendChild(a);
      a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error("Error downloading PDF:", err); }
  };

  const exportCSV = () => {
    if (conversaciones.length === 0) return;
    const headers = ["Contacto", "Telefono", "Chatbot", "Resultado", "Mensajes", "Duracion", "Temas", "Inicio", "Resumen"];
    const rows = conversaciones.map((c) => [
      c.nombre_contacto || "",
      c.numero_telefono,
      c.chatbot_name,
      c.resultado,
      c.total_mensajes,
      c.duracion_minutos ? `${c.duracion_minutos} min` : "",
      (c.temas_detectados || []).join(", "),
      formatAbsTime(c.inicio),
      (c.resumen || "").replace(/"/g, '""'),
    ]);
    const csv = [
      headers.join(","),
      ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
    ].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conversaciones_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async (text, e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopiedPhone(text);
      setTimeout(() => setCopiedPhone(null), 2000);
    } catch (err) { console.error("Copy failed:", err); }
  };

  // Quick date filters
  const applyQuickFilter = (key) => {
    setFechaDesde(""); setFechaHasta("");
    setActiveQuickFilter(key);
  };

  // KPIs simplificados (4 cards, número sólido navy)
  const KPIS = [
    { label: "Total",       value: resumen?.total_conversaciones ?? "—",       icon: <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />, color: "#1e3a5f" },
    { label: "Completadas", value: resumen?.conversaciones_completadas ?? "—", icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,       color: "#16a34a" },
    { label: "Escaladas",   value: resumen?.conversaciones_escaladas ?? "—",   icon: <WarningAmberIcon sx={{ fontSize: 16 }} />,      color: "#f59e0b" },
    { label: "Activas",     value: activas,                                     icon: <FiberManualRecordIcon sx={{ fontSize: 14 }} />, color: activas > 0 ? "#0ea5e9" : "#94a3b8" },
  ];

  const QUICK_FILTERS = [
    { key: "hoy",    label: "Hoy" },
    { key: "ayer",   label: "Ayer" },
    { key: "semana", label: "Semana" },
    { key: "mes",    label: "Mes" },
    { key: "todos",  label: "Todos" },
  ];

  const RESULTADO_FILTERS = [
    { key: "",           label: "Todos los resultados" },
    { key: "completada", label: "Completadas" },
    { key: "escalada",   label: "Escaladas" },
    { key: "timeout",    label: "Timeout" },
  ];

  const hasMore = conversaciones.length < total;
  let lastGroup = null;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Animaciones globales */}
      <style>{`
        @keyframes narapulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(0.85); } }
        @keyframes naraspin  { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#1e3a5f", mb: 0.3, display: "flex", alignItems: "center", gap: 1 }}>
            Conversaciones
            {activas > 0 && (
              <Tooltip title="Hay conversaciones activas — la lista se refresca cada 30s">
                <Chip
                  size="small" icon={<FiberManualRecordIcon sx={{ fontSize: 10 }}/>}
                  label={`${activas} activa${activas > 1 ? "s" : ""}`}
                  sx={{
                    height: 22, fontSize: 11, fontWeight: 700,
                    bgcolor: "#e0f2fe", color: "#0369a1",
                    "& .MuiChip-icon": { color: "#0ea5e9", ml: 0.4, animation: "narapulse 2s ease-in-out infinite" },
                  }}
                />
              </Tooltip>
            )}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#64748b" }}>
            {total} conversación{total === 1 ? "" : "es"} en el rango seleccionado
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Exportar a CSV">
            <span>
              <Button
                size="small" variant="outlined"
                startIcon={<TableChartIcon sx={{ fontSize: 16 }} />}
                onClick={exportCSV} disabled={conversaciones.length === 0}
                sx={{
                  borderColor: "#e2e8f0", color: "#475569",
                  textTransform: "none", fontWeight: 600, fontSize: 12,
                  borderRadius: 2, px: 1.5, py: 0.6,
                  "&:hover": { borderColor: "#1e3a5f", bgcolor: "#f8fafc" },
                  "&.Mui-disabled": { borderColor: "#f1f5f9", color: "#cbd5e1" },
                }}
              >
                CSV
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Actualizar">
            <Button
              size="small" variant="contained"
              startIcon={<RefreshIcon sx={{ fontSize: 16, animation: activas > 0 ? "naraspin 3s linear infinite" : "none" }}/>}
              onClick={() => fetchData()}
              sx={{
                background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
                borderRadius: 2, px: 2, py: 0.6,
                fontSize: 12, fontWeight: 700, textTransform: "none",
                boxShadow: "0 4px 12px rgba(30,58,95,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)" },
              }}
            >
              Actualizar
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* KPIs simplificados */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
        gap: 1.5, mb: 2,
      }}>
        {KPIS.map((k) => (
          <Paper key={k.label} sx={{
            p: 2, borderRadius: 2.5,
            border: "1px solid #e2e8f0", background: "#fff",
            display: "flex", alignItems: "center", gap: 1.5,
          }}>
            <Box sx={{
              width: 34, height: 34, borderRadius: 2,
              background: k.color + "18", color: k.color,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {k.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                fontSize: 11, fontWeight: 700, color: "#94a3b8",
                textTransform: "uppercase", letterSpacing: 0.4,
              }}>
                {k.label}
              </Typography>
              {loading ? (
                <Skeleton variant="text" width={40} height={26} />
              ) : (
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#1e3a5f", letterSpacing: -0.3, lineHeight: 1.1 }}>
                  {k.value}
                </Typography>
              )}
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Búsqueda dominante */}
      <TextField
        fullWidth
        placeholder="Buscar por nombre, teléfono o contenido..."
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        sx={{
          mb: 1.5,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2.5, background: "#fff", fontSize: 14,
            "& fieldset": { borderColor: "#e2e8f0" },
            "&:hover fieldset": { borderColor: "#cbd5e1" },
            "&.Mui-focused fieldset": { borderColor: "#1e3a5f", borderWidth: 2 },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#94a3b8" }} />
            </InputAdornment>
          ),
          endAdornment: busqueda && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setBusqueda("")}>
                <ClearIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* Filtros: quick date + resultado + chatbot */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 1, mb: 1.5, flexWrap: "wrap",
      }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, flex: 1 }}>
          {QUICK_FILTERS.map((q) => {
            const activo = activeQuickFilter === q.key;
            return (
              <Chip
                key={q.key}
                label={q.label}
                size="small"
                onClick={() => applyQuickFilter(q.key)}
                sx={{
                  fontSize: 12, fontWeight: 600, height: 28, cursor: "pointer",
                  color: activo ? "#fff" : "#475569",
                  background: activo ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)" : "#fff",
                  border: activo ? "none" : "1px solid #e2e8f0",
                  "&:hover": {
                    background: activo
                      ? "linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)"
                      : "#f8fafc",
                  },
                }}
              />
            );
          })}
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={resultadoFilter}
              onChange={(e) => setResultadoFilter(e.target.value)}
              displayEmpty
              sx={{
                fontSize: 13, borderRadius: 2, background: "#fff",
                "& .MuiSelect-select": { py: 0.85 },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
              }}
            >
              {RESULTADO_FILTERS.map((r) => (
                <MenuItem key={r.key} value={r.key} sx={{ fontSize: 13 }}>{r.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {chatbots.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <Select
                value={chatbotFilter}
                onChange={(e) => setChatbotFilter(e.target.value)}
                displayEmpty
                sx={{
                  fontSize: 13, borderRadius: 2, background: "#fff",
                  "& .MuiSelect-select": { py: 0.85 },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e3a5f" },
                }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>Todos los chatbots</MenuItem>
                {chatbots.map((cb) => (
                  <MenuItem key={cb.id} value={cb.id} sx={{ fontSize: 13 }}>{cb.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      {/* Lista */}
      {loading ? (
        <Paper sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #e2e8f0", background: "#fff" }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ p: 1.5, borderBottom: i < 5 ? "1px solid #f1f5f9" : "none", display: "flex", gap: 1.5 }}>
              <Skeleton variant="circular" width={38} height={38} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="60%" height={16} />
              </Box>
            </Box>
          ))}
        </Paper>
      ) : conversaciones.length === 0 ? (
        <Paper sx={{
          p: 6, textAlign: "center", borderRadius: 3,
          border: "1px dashed #cbd5e1", background: "#f8fafc",
        }}>
          <ForumIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 1.5 }} />
          <Typography sx={{ color: "#475569", fontSize: 14, fontWeight: 600, mb: 0.5 }}>
            {searchDebounced
              ? `Sin resultados para "${searchDebounced}"`
              : activeQuickFilter && activeQuickFilter !== "todos"
                ? "Sin conversaciones en este rango"
                : "Aún no hay conversaciones"}
          </Typography>
          <Typography sx={{ color: "#94a3b8", fontSize: 12, mb: 2 }}>
            Cuando un usuario chatee con un chatbot, aparecerá aquí
          </Typography>
          {(searchDebounced || (activeQuickFilter && activeQuickFilter !== "todos") || resultadoFilter) && (
            <Button
              size="small"
              onClick={() => { setBusqueda(""); setResultadoFilter(""); setActiveQuickFilter("todos"); }}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              Limpiar filtros
            </Button>
          )}
        </Paper>
      ) : (
        <Paper sx={{
          borderRadius: 3, overflow: "hidden",
          border: "1px solid #e2e8f0", background: "#fff",
        }}>
          {conversaciones.map((conv) => {
            const config = RESULTADO_CONFIG[conv.resultado] || RESULTADO_CONFIG.completada;
            const isExpanded = expandedId === conv.id;
            const isActive = conv.resultado === "activa";
            const group = getDateGroup(conv);
            const showSeparator = group !== lastGroup;
            lastGroup = group;

            return (
              <React.Fragment key={conv.id}>
                {showSeparator && (
                  <Box sx={{
                    position: "sticky", top: 0, zIndex: 2,
                    px: 2, py: 0.6,
                    background: "linear-gradient(180deg, #f8fafc, #f1f5f9)",
                    borderBottom: "1px solid #e2e8f0",
                    borderTop: "1px solid #e2e8f0",
                  }}>
                    <Typography sx={{
                      fontSize: 11, fontWeight: 800, color: "#475569",
                      letterSpacing: 1.2, textTransform: "uppercase",
                    }}>
                      {group}
                    </Typography>
                  </Box>
                )}

                <ConversacionRow
                  conv={conv}
                  config={config}
                  isExpanded={isExpanded}
                  isActive={isActive}
                  copiedPhone={copiedPhone}
                  onToggle={() => loadMessages(conv.id)}
                  onCopy={(e) => copyToClipboard(conv.numero_telefono, e)}
                  onDownload={(e) => downloadPDF(conv.id, e)}
                />

                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2.5, pb: 2, pt: 0.5, background: "#fafbfc", borderBottom: "1px solid #f1f5f9" }}>
                    {conv.resumen && (
                      <Box sx={{
                        mt: 1.25, mb: 1.25, p: 1.25, borderRadius: 1.5,
                        background: "#eef2ff", border: "1px solid #c7d2fe",
                      }}>
                        <Typography sx={{
                          fontSize: 10, fontWeight: 800, color: "#4f46e5",
                          textTransform: "uppercase", letterSpacing: 0.6, mb: 0.4,
                        }}>
                          Resumen
                        </Typography>
                        <Typography sx={{ fontSize: 12.5, color: "#1e293b", lineHeight: 1.5 }}>
                          {conv.resumen}
                        </Typography>
                      </Box>
                    )}
                    {loadingMsgs ? (
                      <Box sx={{ py: 1.5 }}>
                        <Skeleton variant="text" width="60%" />
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="50%" />
                      </Box>
                    ) : expandedMsgs.length === 0 ? (
                      <Typography sx={{ color: "#94a3b8", fontSize: 12.5, textAlign: "center", py: 2 }}>
                        Sin mensajes disponibles
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.85, maxHeight: 400, overflowY: "auto", pr: 0.5 }}>
                        {expandedMsgs.map((msg, i) => (
                          <MensajeRow key={i} msg={msg} />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </React.Fragment>
            );
          })}
        </Paper>
      )}

      {/* Cargar más */}
      {hasMore && conversaciones.length > 0 && (
        <Box sx={{ textAlign: "center", mt: 1.5 }}>
          <Button
            onClick={loadMore}
            disabled={loadingMore}
            size="small"
            startIcon={loadingMore ? <CircularProgress size={14} sx={{ color: "#94a3b8" }}/> : null}
            sx={{
              color: "#475569",
              fontSize: 12.5,
              fontWeight: 600,
              textTransform: "none",
              px: 3, py: 0.85,
              borderRadius: 2,
              background: "#fff",
              border: "1px solid #e2e8f0",
              "&:hover": { background: "#f8fafc", borderColor: "#cbd5e1" },
            }}
          >
            {loadingMore ? "Cargando..." : `Cargar más (${total - conversaciones.length} restantes)`}
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="info" variant="filled"
          sx={{
            borderRadius: 2,
            background: "linear-gradient(135deg, #1e3a5f, #2d5a87)",
            boxShadow: "0 8px 24px rgba(30,58,95,0.3)",
            fontSize: 13, fontWeight: 600,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Fila de conversación — estilo Pacientes con avatar gradient
// ──────────────────────────────────────────────────────────────────────────
function ConversacionRow({ conv, config, isExpanded, isActive, copiedPhone, onToggle, onCopy, onDownload }) {
  const nombre = conv.nombre_contacto || conv.numero_telefono;
  const color = hashColor(nombre);
  const inits = iniciales(nombre);
  const phoneCopied = copiedPhone === conv.numero_telefono;

  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex", alignItems: "flex-start", gap: 1.25,
        px: 2, py: 1.2,
        cursor: "pointer",
        borderBottom: isExpanded ? "none" : "1px solid #f1f5f9",
        background: isExpanded ? "#fafbfc" : "transparent",
        transition: "background 0.15s",
        "&:hover": { background: "#f8fafc" },
        position: "relative",
      }}
    >
      {/* Indicador izquierdo del estado */}
      <Box sx={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
        background: config.color,
        ...(isActive ? { animation: "narapulse 2s ease-in-out infinite" } : {}),
      }}/>

      {/* Avatar */}
      <Avatar sx={{
        width: 38, height: 38,
        bgcolor: color.bg, color: color.fg,
        fontSize: 13, fontWeight: 800, flexShrink: 0, ml: 0.4,
      }}>
        {inits}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Línea 1: nombre + chip resultado + teléfono */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.7, mb: 0.25, flexWrap: "wrap" }}>
          <Typography sx={{
            fontSize: 14, fontWeight: 700, color: "#1e293b",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            maxWidth: { xs: 180, sm: 260, md: 360 },
          }}>
            {nombre}
          </Typography>
          <Chip
            size="small"
            icon={config.icon}
            label={config.label}
            sx={{
              height: 19, fontSize: 10.5, fontWeight: 700,
              bgcolor: `${config.color}15`, color: config.color,
              "& .MuiChip-icon": { color: config.color, ml: 0.4 },
              "& .MuiChip-label": { px: 0.6 },
              ...(isActive ? { animation: "narapulse 2s ease-in-out infinite" } : {}),
            }}
          />
          {/* Tipo de contacto: turismo-específico (TURISTA / AGENCIA / INTERMEDIARIO) */}
          {conv.tipo_contacto === 'intermediario' && (
            <Chip size="small" label="INTERMEDIARIO"
              sx={{ height: 19, fontSize: 9.5, fontWeight: 700, bgcolor: '#ede9fe', color: '#6d28d9', "& .MuiChip-label": { px: 0.6 } }} />
          )}
          {conv.tipo_contacto === 'agencia' && (
            <Chip size="small" label="AGENCIA"
              sx={{ height: 19, fontSize: 9.5, fontWeight: 700, bgcolor: '#fef3c7', color: '#a16207', "& .MuiChip-label": { px: 0.6 } }} />
          )}
          {conv.tipo_contacto === 'turista' && (
            <Chip size="small" label="TURISTA"
              sx={{ height: 19, fontSize: 9.5, fontWeight: 700, bgcolor: '#dbeafe', color: '#1d4ed8', "& .MuiChip-label": { px: 0.6 } }} />
          )}
          {conv.nombre_contacto && (
            <Tooltip title={phoneCopied ? "¡Copiado!" : "Copiar teléfono"}>
              <Box
                onClick={onCopy}
                sx={{
                  display: "flex", alignItems: "center", gap: 0.3,
                  px: 0.6, py: 0.05, borderRadius: 0.85,
                  cursor: "pointer",
                  background: phoneCopied ? "#dcfce7" : "#f1f5f9",
                  border: "1px solid",
                  borderColor: phoneCopied ? "#bbf7d0" : "#e2e8f0",
                  "&:hover": { background: phoneCopied ? "#dcfce7" : "#eef2ff" },
                }}
              >
                {phoneCopied
                  ? <CheckCircleIcon sx={{ fontSize: 10, color: "#16a34a" }}/>
                  : <ContentCopyIcon sx={{ fontSize: 10, color: "#64748b" }}/>}
                <Typography sx={{
                  fontSize: 10.5, fontFamily: "ui-monospace, monospace",
                  color: phoneCopied ? "#16a34a" : "#475569", fontWeight: 600,
                }}>
                  {conv.numero_telefono}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Línea 2: metadata */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, color: "#64748b", fontSize: 11.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
            <SmartToyIcon sx={{ fontSize: 11 }}/>
            <span>{conv.chatbot_name}</span>
          </Box>
          <Tooltip title={formatAbsTime(conv.inicio)}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
              <AccessTimeIcon sx={{ fontSize: 11 }}/>
              <span>{formatRelativeTime(conv.inicio)}</span>
            </Box>
          </Tooltip>
          {conv.duracion_minutos > 0 && (
            <Typography sx={{ fontSize: 11.5 }}>· {conv.duracion_minutos} min</Typography>
          )}
          <Typography sx={{ fontSize: 11.5 }}>· {conv.total_mensajes} msg</Typography>
          {conv.tokens_consumidos > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color: "#7c3aed", fontWeight: 600 }}>
              <TokenIcon sx={{ fontSize: 11 }}/>
              <span>
                {conv.tokens_consumidos >= 1000
                  ? `${(conv.tokens_consumidos / 1000).toFixed(1)}K`
                  : conv.tokens_consumidos}
              </span>
            </Box>
          )}
        </Box>

        {/* Línea 3: temas detectados */}
        {conv.temas_detectados?.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.4, mt: 0.5, flexWrap: "wrap" }}>
            {conv.temas_detectados.map((t) => {
              const ti = temaInfo(t);
              return (
                <Box key={t} sx={{
                  px: 0.7, py: 0.05, borderRadius: 0.85,
                  background: ti.bg, color: ti.fg,
                  fontSize: 10, fontWeight: 700, letterSpacing: 0.2,
                }}>
                  {ti.label}
                </Box>
              );
            })}
          </Box>
        )}

        {/* Línea 4: preview de resumen (solo si NO expandido) */}
        {conv.resumen && !isExpanded && (
          <Typography sx={{
            fontSize: 12, color: "#64748b", mt: 0.5, lineHeight: 1.4,
            overflow: "hidden", textOverflow: "ellipsis",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {conv.resumen}
          </Typography>
        )}
      </Box>

      {/* Acciones */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
        <Tooltip title="Descargar PDF">
          <IconButton size="small" onClick={onDownload}
            sx={{ color: "#cbd5e1", p: 0.5, "&:hover": { color: "#1e3a5f", bgcolor: "#eef2ff" } }}>
            <DownloadIcon sx={{ fontSize: 16 }}/>
          </IconButton>
        </Tooltip>
        <IconButton size="small" sx={{ color: "#94a3b8", p: 0.5 }}>
          {isExpanded
            ? <ExpandLessIcon sx={{ fontSize: 18 }}/>
            : <ExpandMoreIcon sx={{ fontSize: 18 }}/>}
        </IconButton>
      </Box>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Burbuja de mensaje individual (preview en expand)
// ──────────────────────────────────────────────────────────────────────────
function MensajeRow({ msg }) {
  const isUser = msg.role === "user";
  return (
    <Box sx={{ display: "flex", gap: 0.85, alignItems: "flex-start" }}>
      <Box sx={{
        width: 22, height: 22, borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, mt: 0.2,
        background: isUser
          ? "linear-gradient(135deg, #1e3a5f, #2d5a87)"
          : "linear-gradient(135deg, #6366f1, #4f46e5)",
      }}>
        {isUser
          ? <PersonIcon sx={{ fontSize: 12, color: "#fff" }} />
          : <SmartToyIcon sx={{ fontSize: 12, color: "#fff" }} />}
      </Box>
      <Box sx={{
        flex: 1, p: 1, px: 1.25, borderRadius: 1.5,
        background: isUser ? "#eef2ff" : "#fff",
        border: "1px solid",
        borderColor: isUser ? "#c7d2fe" : "#e2e8f0",
      }}>
        <Typography sx={{
          fontSize: 9.5, fontWeight: 800, mb: 0.2,
          color: isUser ? "#4f46e5" : "#94a3b8",
          textTransform: "uppercase", letterSpacing: 0.5,
        }}>
          {isUser ? "Turista" : "NARA"}
        </Typography>
        <Typography sx={{
          fontSize: 12, color: "#1e293b",
          whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5,
        }}>
          {msg.content}
        </Typography>
      </Box>
    </Box>
  );
}
